import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Table,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { FileDownload, SaveAlt, CloudUpload, DeleteOutline } from '@mui/icons-material'
import ExcelJS from 'exceljs'
import { indexedDbStorage } from '../../../utility/indexedDbStorage'
import moment from 'moment'
import { FIELD_TYPES, FORMATS } from '../../../utility/constants'
import LoadingOverlay from '../../../components/core-ui/LoadingOverlay'
import { TableVirtuoso } from 'react-virtuoso'
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import { useConfirmAction } from '../../../hooks/useConfirmAction'
import { isFieldHidden } from '../utils/fieldUtil'
import { useAllValues, useProcessContext } from '../../../store'
import { getFieldIdFromKey } from '../utils/utils'

const DATE_FORMATS = [
  'DD MMM YYYY',
  'DD MMM YYYY, hh:mm A',
  'DD-MM-YYYY',
  'DD/MM/YYYY',
  'YYYY-MM-DD',
  'YYYY-MM-DD HH:mm',
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DDTHH:mm:ss',
]

const isFieldDisabledImpl = () => false

const getMatchPercentage = (a = '', b = '') => {
  if (!a || !b) return 0

  const matrix = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0))

  for (let i = 0; i <= a.length; i++) matrix[i][0] = i
  for (let j = 0; j <= b.length; j++) matrix[0][j] = j

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      matrix[i][j] =
        a[i - 1] === b[j - 1]
          ? matrix[i - 1][j - 1]
          : 1 + Math.min(matrix[i - 1][j], matrix[i][j - 1], matrix[i - 1][j - 1])
    }
  }

  const distance = matrix[a.length][b.length]
  const maxLen = Math.max(a.length, b.length)
  return Math.round(((maxLen - distance) / maxLen) * 100)
}

const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  // fallback (RFC4122-ish)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

const UploadExcelDataExtended = ({
  field,
  columns,
  isOpen,
  onClose,
  existingTabularData = {},
  tableSequenceAndHeadersExisting = [],
  hiddenColumnIds,
  isFieldDisabled = isFieldDisabledImpl,
  triggerAutoComputeRowWise = async () => ({}),
  syncChildRecursively = (v) => v,
  onSave = async () => {},
}) => {
  const { confirm, ConfirmDialog } = useConfirmAction()
  const fileInputRef = useRef(null)
  const [tableSequenceAndHeaders] = useState(tableSequenceAndHeadersExisting || [])

  const [excelHeaders, setExcelHeaders] = useState([]) // [{ colIndex }]
  const [excelRowData, setExcelRowData] = useState([]) // [{ colIndex: value }]
  const [excelColMap, setExcelColMap] = useState({}) // { colIndex: fieldId }

  const [errors, setErrors] = useState([])
  const [isDragging, setIsDragging] = useState(false)
  const [idColMap, setIdColMap] = useState({})
  const [listMatchMeta, setListMatchMeta] = useState({})
  const [autoCorrectedCells, setAutoCorrectedCells] = useState({})
  const [rejectedAutoCorrections, setRejectedAutoCorrections] = useState({})
  const [loading, setLoading] = useState(false)
  const [listIdValueMap, setListIdValueMap] = useState({})
  const [prefilledColumns, setPrefilledColumns] = useState([])
  const [editableColumns, setEditableColumns] = useState([])
  const [listCache, setListCache] = useState({})
  const processContext = useProcessContext((state) => state.processContext)
  const conditionalEnableDisable = processContext?.conditionalEnableDisable
  const allValues = useAllValues((state) => state.allValues)

  useEffect(() => {
    const loadListCache = async () => {
      const cache = {}

      for (const col of columns) {
        if (col.data_structure === FIELD_TYPES.LIST) {
          cache[col.id] = await getListItems(col)
        }
      }

      setListCache(cache)
    }

    loadListCache()
  }, [columns])

  useEffect(() => {
    const norm = columns.map((col) => {
      const isDisabled = col.is_read_only || col.is_computed
      return { ...col, disabled: isDisabled }
    })

    // LEFT = READ ONLY
    setPrefilledColumns(
      norm
        .filter((col) => col.is_read_only)
        .filter((it) => !it.is_computed)
        .filter((it) => isColumnVisible(it)),
    )

    // RIGHT = EDITABLE
    setEditableColumns(
      norm
        .filter((col) => !col.is_read_only)
        .filter((it) => !it.is_computed)
        .filter((it) => isColumnVisible(it)),
    )
  }, [columns])

  useEffect(() => {
    ;(async () => {
      const listIdValueMap = {}
      for (const col of columns) {
        if (col.data_structure === FIELD_TYPES.LIST)
          listIdValueMap[col.id] = mapIdToLabel(await getListItems(col))
      }
      setListIdValueMap(listIdValueMap)
    })()
  }, [columns])

  useEffect(() => {
    setIdColMap(
      columns.reduce((acc, cur) => {
        acc[cur.id] = cur
        return acc
      }, {}),
    )
  }, [columns])

  const isColumnVisible = (it) => {
    return !isFieldHidden(it, processContext?.hiddenFields, hiddenColumnIds, {
      allValues,
      conditionalEnableDisable,
    })
  }

  const runMatchCheck = () => {
    const meta = {}
    const updatedRows = [...excelRowData]
    const newAutoCorrections = {}

    for (let rowIndex = 0; rowIndex < updatedRows.length; rowIndex++) {
      const row = updatedRows[rowIndex]
      const rowKey = row.__rowKey__

      for (const [colIndex, fieldId] of Object.entries(excelColMap)) {
        const alreadyAutoCorrected = autoCorrectedCells[rowKey]?.[colIndex]
        if (alreadyAutoCorrected) {
          continue
        }

        const column = idColMap[fieldId]
        if (!column || column.data_structure !== 'list') continue

        const cellValue = row[colIndex]
        if (!cellValue) continue

        const listItems = listCache[fieldId] || []
        if (!listItems.length) continue

        // skip for matched value
        const normalizedCell = normalizeHeader(cellValue)
        const exactMatch = listItems.find((item) => normalizeHeader(item.label) === normalizedCell)

        if (exactMatch) {
          if (meta[rowKey]?.[colIndex]) {
            delete meta[rowKey][colIndex]
          }
          continue
        }

        let best = { percentage: 0, label: '' }

        for (const item of listItems) {
          // matching score calculation
          const score = getMatchPercentage(normalizeHeader(cellValue), normalizeHeader(item.label))
          if (score > best.percentage) {
            best = { percentage: score, label: item.label }
          }
        }

        if (normalizeHeader(cellValue) === normalizeHeader(best.label)) {
          continue
        }

        if (best.percentage < 100) {
          meta[rowKey] ??= {}

          const isRejected = rejectedAutoCorrections[rowKey]?.[colIndex]

          meta[rowKey][colIndex] = {
            ...best,
            autoApplied: !isRejected && best.percentage >= 60,
            originalValue: cellValue,
          }

          // ✅ Auto apply (only if not rejected)
          if (!isRejected && best.percentage >= 60) {
            updatedRows[rowIndex] = {
              ...updatedRows[rowIndex],
              [colIndex]: best.label,
            }

            newAutoCorrections[rowKey] ??= {}
            newAutoCorrections[rowKey][colIndex] = {
              originalValue: cellValue,
              label: best.label,
              percentage: best.percentage,
            }

            meta[rowKey][colIndex].resolved = true
          }
        }
      }
    }

    // update metadata
    Object.keys(meta).forEach((rowKey) => {
      Object.keys(meta[rowKey]).forEach((colIndex) => {
        const row = updatedRows.find((r) => r.__rowKey__ === rowKey)
        const val = row?.[colIndex]

        const listItems = listCache[excelColMap[colIndex]] || []

        const isNowExact = listItems.find(
          (item) => normalizeHeader(item.label) === normalizeHeader(val),
        )

        if (isNowExact) {
          meta[rowKey][colIndex] = {
            ...meta[rowKey][colIndex],
            resolved: true,
          }
        }
      })

      if (!Object.keys(meta[rowKey]).length) delete meta[rowKey]
    })

    const isChanged = JSON.stringify(updatedRows) !== JSON.stringify(excelRowData)
    if (isChanged) {
      setExcelRowData(updatedRows)
    }

    setAutoCorrectedCells(newAutoCorrections)
    setListMatchMeta(meta)
  }

  const runValidation = () => {
    const validationErrors = []

    excelRowData.forEach((row, rowIndex) => {
      const rowKey = row.__rowKey__

      Object.entries(excelColMap).forEach(([colIndex, fieldId]) => {
        const column = idColMap[fieldId]
        if (!column) return

        const value = row[colIndex]

        const listItems = listCache[fieldId] || []

        const error = validateCell(value, column, listItems)

        if (error) {
          validationErrors.push({
            rowKey,
            colIndex,
            message: error,
            rowIndex,
          })
        }
      })
    })

    setErrors(validationErrors)
  }

  useEffect(() => {
    if (!excelRowData.length) return
    if (!Object.keys(excelColMap).length) return
    if (!Object.keys(listCache).length) return

    runMatchCheck()
  }, [excelColMap, listCache])

  useEffect(() => {
    if (!excelRowData.length) return
    if (!Object.keys(excelColMap).length) return
    if (!Object.keys(listCache).length) return

    runValidation()
  }, [excelRowData, excelColMap, listCache])

  useEffect(() => {
    if (!excelHeaders.length || !columns.length) return

    const newMap = {}

    excelHeaders.forEach((h) => {
      const headerText = excelRowData[0]?.[h.colIndex]
      if (!headerText) return

      let best = { id: null, score: 0 }

      editableColumns.forEach((col) => {
        const score = getMatchPercentage(
          normalizeHeader(headerText),
          normalizeHeader(col.field_template_name),
        )
        if (score > best.score) best = { id: col.id, score }
      })

      if (best.score >= 60) {
        newMap[h.colIndex] = best.id
      }
    })

    setExcelColMap((prev) => ({ ...newMap, ...prev }))
  }, [excelHeaders, columns])

  const mergedRows = useMemo(() => {
    const maxLength = Math.max(tableSequenceAndHeadersExisting.length, excelRowData.length)

    return Array.from({ length: maxLength }).map((_, index) => {
      const existingRowMeta = tableSequenceAndHeadersExisting[index]
      const existingRowId = existingRowMeta?.row

      return {
        index,
        existingRowId: Number(existingRowId),
        existingData: existingRowId ? existingTabularData[`tr_${existingRowId}`] : null,
        uploadedData: excelRowData[index] || null,
        uploadedRowKey: excelRowData[index]?.__rowKey__,
      }
    })
  }, [excelRowData, existingTabularData, tableSequenceAndHeadersExisting])

  /* ================= HELPERS ================= */

  const revertAutoMatch = (rowKey, colIndex) => {
    const meta = autoCorrectedCells[rowKey]?.[colIndex]
    if (!meta) return

    // restore original value
    setExcelRowData((prev) =>
      prev.map((r) => (r.__rowKey__ === rowKey ? { ...r, [colIndex]: meta.originalValue } : r)),
    )

    // mark as explicitly rejected
    setRejectedAutoCorrections((prev) => ({
      ...prev,
      [rowKey]: {
        ...(prev[rowKey] || {}),
        [colIndex]: true,
      },
    }))

    // remove auto-correct badge
    setAutoCorrectedCells((prev) => {
      const copy = { ...prev }
      delete copy[rowKey]?.[colIndex]
      if (!Object.keys(copy[rowKey] || {}).length) delete copy[rowKey]
      return copy
    })
  }

  const applyBestMatch = (rowKey, colIndex) => {
    const match = listMatchMeta[rowKey]?.[colIndex]
    if (!match) return

    const currentRows = [...excelRowData]
    const originalNormalized = normalizeHeader(match.originalValue)

    setExcelRowData((prev) =>
      prev.map((r) => {
        const cellVal = r[colIndex]

        if (normalizeHeader(cellVal) === originalNormalized) {
          return {
            ...r,
            [colIndex]: match.label,
          }
        }

        return r
      }),
    )

    setAutoCorrectedCells((prev) => {
      const updated = { ...prev }

      currentRows.forEach((r) => {
        if (normalizeHeader(r[colIndex]) === originalNormalized) {
          const rk = r.__rowKey__

          updated[rk] ??= {}
          updated[rk][colIndex] = {
            originalValue: r[colIndex],
            label: match.label,
            percentage: match.percentage,
            manuallyApplied: true,
          }
        }
      })

      return updated
    })

    setRejectedAutoCorrections((prev) => {
      const copy = { ...prev }

      currentRows.forEach((r) => {
        if (normalizeHeader(r[colIndex]) === originalNormalized) {
          const rk = r.__rowKey__
          delete copy[rk]?.[colIndex]
          if (!Object.keys(copy[rk] || {}).length) delete copy[rk]
        }
      })

      return copy
    })

    setListMatchMeta((prev) => {
      const copy = { ...prev }

      Object.keys(copy).forEach((rk) => {
        if (normalizeHeader(copy[rk]?.[colIndex]?.originalValue) === originalNormalized) {
          copy[rk][colIndex].resolved = true
        }
      })

      return copy
    })
  }

  const normalizeHeader = (str = '') =>
    str
      ?.toString()
      .toLowerCase()
      .replace(/\(.*?\)/g, '')
      .replace(/\*/g, '')
      .replace(/[_\-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()

  const validateCell = (value, column, listItems = []) => {
    if (value === null || value === undefined || value === '') return null

    switch (column.data_structure) {
      case FIELD_TYPES.NUMBER:
      case FIELD_TYPES.CURRENCY:
        return isNaN(Number(value)) ? 'Invalid number' : null

      case FIELD_TYPES.DATE:
      case FIELD_TYPES.DATETIME:
        return moment(value, DATE_FORMATS, false).isValid() ? null : 'Invalid date format'

      case FIELD_TYPES.LIST: {
        const normalized = normalizeHeader(value)
        const exists = listItems.some((item) => normalizeHeader(item.label) === normalized)
        return exists ? null : 'Value not in list'
      }

      default:
        return null
    }
  }

  const getListItems = async (column) => {
    const listDataId = `list_data_${column.list_id}`
    const listDataJson = await indexedDbStorage.getItem(listDataId)
    return listDataJson.map((it) => ({ id: it.id, label: it.value }))
  }

  const mapLabelToId = (listItems) =>
    listItems.reduce((acc, item) => {
      acc[item.label] = item.id
      return acc
    }, {})

  /* ================= DOWNLOAD TEMPLATE ================= */

  const addListDropdownSheet = (workbook, listItems, columnKey) => {
    const sheetName = `__list_${columnKey}`.replace(/[^a-zA-Z0-9_]/g, '')
    const sheet = workbook.addWorksheet(sheetName)

    sheet.columns = [
      { header: 'Label', key: 'label', width: 30 },
      { header: 'ID', key: 'id', width: 10 },
    ]

    listItems.forEach((item) => sheet.addRow(item))
    sheet.state = 'hidden'
    return sheetName
  }

  const getExcelColumnName = (colIndex) => {
    let name = ''
    while (colIndex > 0) {
      const rem = (colIndex - 1) % 26
      name = String.fromCharCode(65 + rem) + name
      colIndex = Math.floor((colIndex - 1) / 26)
    }
    return name
  }

  const applyDropdownValidation = (worksheet, columnIndex, listSheetName, totalRows = 1000) => {
    const colLetter = getExcelColumnName(columnIndex)

    worksheet.dataValidations.add(`${colLetter}2:${colLetter}${totalRows}`, {
      type: 'list',
      allowBlank: true,
      formulae: [`='${listSheetName}'!$A$2:$A$1000`],
    })
  }

  const mapIdToLabel = (listItems) =>
    listItems.reduce((acc, item) => {
      acc[item.id] = item.label
      return acc
    }, {})

  const softDisableCell = (cell, reason) => {
    // block typing / paste
    cell.dataValidation = {
      type: 'custom',
      formulae: ['FALSE'],
      showErrorMessage: true,
      errorTitle: 'Editing not allowed',
      error: reason || 'This field cannot be edited.',
    }

    // visual cue
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF2F2F2' },
    }

    cell.font = {
      color: { argb: 'FF888888' },
      italic: true,
    }

    // hover hint
    cell.note = reason || 'Auto-calculated field'
  }

  const downloadExcelTemplate = async () => {
    setLoading(true)
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet(field.field_template_name)

    const sortedColumns = [...columns].sort((a, b) => a.sequence - b.sequence)

    /* ---------------------------------- */
    /* Columns (NO row_id, NO hint row)   */
    /* ---------------------------------- */
    worksheet.columns = sortedColumns
      .filter((it) => isColumnVisible(it))
      .map((col) => ({
        header: col.is_required ? `${col.field_template_name} *` : col.field_template_name,
        key: `f_${col.id}`,
        width: 25,
      }))

    /* ---------------------------------- */
    /* Dropdown sheets                    */
    /* ---------------------------------- */
    for (let i = 0; i < sortedColumns.length; i++) {
      const col = sortedColumns[i]

      if (col.data_structure === 'list') {
        const listItems = await getListItems(col)

        // 🚨 Skip if empty (prevents ExcelJS crash)
        if (!listItems || listItems.length === 0) continue

        const listSheetName = addListDropdownSheet(workbook, listItems, col.id)

        applyDropdownValidation(worksheet, i + 1, listSheetName)
      }
    }

    /* ---------------------------------- */
    /* List ID → Label map                */
    /* ---------------------------------- */
    const listLabelMaps = {}

    for (const col of sortedColumns) {
      if (col.data_structure === 'list') {
        const listItems = await getListItems(col)
        listLabelMaps[col.id] = mapIdToLabel(listItems)
      }
    }

    /* ---------------------------------- */
    /* Populate existing rows             */
    /* Order strictly from table sequence */
    /* ---------------------------------- */
    tableSequenceAndHeaders?.forEach((seq) => {
      const rowValues = existingTabularData?.[`tr_${seq.row}`] || {}
      const row = {}

      sortedColumns.forEach((col) => {
        let value = rowValues[`f_${col.id}`] ?? null

        // list → label
        if (col.data_structure === 'list' && value != null) {
          value = listLabelMaps[col.id]?.[value] ?? null
        }

        row[`f_${col.id}`] = value
      })

      worksheet.addRow(row)
    })

    /* ---------------------------------- */
    /* Apply soft-disable per cell        */
    /* ---------------------------------- */
    sortedColumns.forEach((col, colIndex) => {
      const excelColIndex = colIndex + 1

      worksheet.getColumn(excelColIndex).eachCell((cell, rowNumber) => {
        if (rowNumber === 1) return // header row

        const seqIndex = rowNumber - 2
        const rowId = tableSequenceAndHeaders?.[seqIndex]?.row

        const disabled = isFieldDisabled(col, rowId)
        if (disabled) {
          softDisableCell(cell, 'Auto-calculated field')
        }
      })
    })

    /* ---------------------------------- */
    /* UX polish                          */
    /* ---------------------------------- */
    worksheet.views = [{ state: 'frozen', ySplit: 1 }]

    /* ---------------------------------- */
    /* Download                           */
    /* ---------------------------------- */
    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })

    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `${field.field_template_name}_template.xlsx`
    link.click()

    setLoading(false)
  }

  /* ================= UPLOAD ================= */

  const handleExcelUpload = async (e) => {
    setLoading(true)
    const file = e.target.files?.[0]
    if (!file) return

    const workbook = new ExcelJS.Workbook()
    await workbook.xlsx.load(await file.arrayBuffer())
    const worksheet = workbook.worksheets[0]

    const headers = []
    const rows = []

    worksheet.eachRow((row, rowNumber) => {
      const rowData = {}

      row.eachCell((cell, colIndex) => {
        let value
        if (cell.type === ExcelJS.ValueType.Date) {
          const d = cell.value
          value = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')} ${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}:${String(d.getUTCSeconds()).padStart(2, '0')}`
          value = moment(value).format(FORMATS.DATETIME)
        } else {
          value = cell.text
        }

        rowData[colIndex] = value
        if (rowNumber === 1) headers.push({ colIndex })
      })

      rows.push({
        __rowKey__: generateUUID(),
        ...rowData,
      })
    })

    setExcelHeaders(headers)
    setExcelRowData(rows)
    setExcelColMap({})
    setErrors([])
    setLoading(false)
  }

  /* ================= DELETE ================= */
  const deleteRow = (rowKey) => {
    setExcelRowData((prev) => prev.filter((r) => r.__rowKey__ !== rowKey))
  }

  const deleteColumn = (colIndex) => {
    setLoading(true)
    setExcelHeaders((h) => h.filter((c) => c.colIndex !== colIndex))
    setExcelColMap((m) => {
      const copy = { ...m }
      delete copy[colIndex]
      return copy
    })
    setExcelRowData((rows) =>
      rows.map((r) => {
        const copy = { ...r }
        delete copy[colIndex]
        return copy
      }),
    )
    setLoading(false)
  }

  /* ================= VALIDATION ================= */

  const areAllExcelColumnsMapped = () => excelHeaders.every((h) => Boolean(excelColMap[h.colIndex]))

  const hasDuplicateMappedColumns = () => {
    const values = Object.values(excelColMap).filter(Boolean)
    return new Set(values).size !== values.length
  }

  const canSave = () => {
    if (!excelHeaders.length) return false
    if (!areAllExcelColumnsMapped()) return false
    if (hasDuplicateMappedColumns()) return false
    if (errors.length > 0) return false
    if (excelRowData.length <= 0) return false
    return true
  }

  /* ================= PROCESS (UNCHANGED LOGIC) ================= */

  const getNextRowId = (row, existingRowIds) => {
    if (row.existingRowId) {
      return row.existingRowId
    } else {
      return (Math.max(...existingRowIds.map(Number)) || 0) + 1
    }
  }

  const processExcelData = async () => {
    setLoading(true)
    const listMaps = {}
    for (const col of columns) {
      if (col.data_structure === 'list') listMaps[col.id] = mapLabelToId(await getListItems(col))
    }

    // Important:: skip the extra rows from excel
    const existingRowIds = tableSequenceAndHeadersExisting.map((it) => Number(it.row))
    const excelRowIds = mergedRows
      .filter((it) => it.uploadedData != null)
      .map((it) => String(it.existingRowId))
      .map(Number)

    const excelHaveLessRow = excelRowData.length < Object.keys(existingTabularData).length
    const excelHaveMoreRow = excelRowData.length > Object.keys(existingTabularData).length

    // normalizedData = existing row data appends excel row data
    // only editable column -> replacement is disabled for readonly cells
    const _existingRowIds = []

    let normalizedData = mergedRows.map((row, index) => {
      const rowId = getNextRowId(row, _existingRowIds)
      const existing = row.existingData || {}

      let normalized = {
        __row_id__: rowId,
        ...existing,
      }

      _existingRowIds.push(rowId)

      Object.entries(excelColMap).forEach(([colIndex, fieldId]) => {
        const col = idColMap[fieldId]

        // 🔒 HARD LOCK READ-ONLY FIELDS
        if (isFieldDisabled(col, rowId)) {
          normalized[`f_${col.id}`] = existing[`f_${col.id}`]
          return
        }

        let value = row.uploadedData?.[colIndex]

        if (typeof value === 'object' && value?.text) value = value.text

        if (col.data_structure === 'list') {
          value = listMaps[col.id]?.[value] ?? null
        }

        if (['date', 'datetime'].includes(col.data_structure)) {
          value = moment(value).format(FORMATS.DATETIME)
        }

        // fallback to existing
        if (value === undefined || value === null || value === '') {
          if (isFieldDisabled(col, rowId)) {
            value = existing[`f_${col.id}`]
          }
        }

        normalized[`f_${col.id}`] = value
      })

      return normalized
    })

    if (!normalizedData?.length) return

    // New logic for keep/remove data in existing or from excel
    let needCleanInsert = false // need this to delete irrelevant keys related to current table
    let confirmationMessage = ''

    if (excelHaveLessRow) {
      if (field.allow_delete_rows) {
        normalizedData = normalizedData.filter(
          (it) => it.__row_id__ && excelRowIds.includes(Number(it.__row_id__)),
        )
        needCleanInsert = true
        confirmationMessage = `Saving will remove ${existingRowIds.length - excelRowIds.length} row(s) from the existing table, as row deletion is enabled.`
      } else {
        normalizedData = normalizedData.map((row) => {
          if (excelRowIds.includes(Number(row.__row_id__))) {
            return row
          } else {
            return {
              ...existingTabularData[`tr_${row.__row_id__}`],
              __row_id__: row.__row_id__,
            }
          }
        })
        confirmationMessage = `Saving will override data for the first ${excelRowIds.length} row(s). No rows will be deleted.`
      }
    } else if (excelHaveMoreRow) {
      if (!field.allow_add_rows) {
        normalizedData = normalizedData.filter(
          (it) => it.__row_id__ && existingRowIds.includes(Number(it.__row_id__)),
        )
        confirmationMessage = `Extra ${excelRowIds.length - existingRowIds.length} row(s) in the upload will be ignored, as adding new rows is disabled.`
      } else {
        confirmationMessage = `Extra ${excelRowIds.length - existingRowIds.length} row(s) in the upload will be added to the existing table, as adding new rows is enabled.`
      }
    } else {
      confirmationMessage = `Saving will override the entire table data.`
    }

    setLoading(false)
    if (confirmationMessage) {
      confirm({
        actionFn: async () => {
          await saveConfirmed(normalizedData, needCleanInsert)
        },
        heading: 'Confirmation',
        message: confirmationMessage,
        confirmText: 'Save',
      })
    } else {
      await saveConfirmed(normalizedData, needCleanInsert)
    }
  }

  const saveConfirmed = async (normalizedData, needCleanInsert) => {
    setLoading(true)
    const finalRows = []
    for (let i = 0; i < normalizedData.length; i++) {
      let row = normalizedData[i]

      const c1 = await triggerAutoComputeRowWise(row, i, row.__row_id__)
      const c2 = await triggerAutoComputeRowWise({ ...row, ...c1 }, i, row.__row_id__)
      const c3 = await triggerAutoComputeRowWise({ ...row, ...c2 }, i, row.__row_id__)

      // remove calculated fields which are not the part of table subfield
      const calculatedRowValues = Object.entries(c3).reduce((acc, [key, value]) => {
        const colId = getFieldIdFromKey(key)?.id
        const isColumn = columns.some((col) => col.id === colId)
        if (isColumn) {
          return { ...acc, [key]: value }
        }
        return acc
      }, {})

      finalRows.push(syncChildRecursively({ ...row, ...calculatedRowValues }))
    }

    const tabularData = {}
    let seq = []

    let rowId = 1
    const payload = finalRows.reduce((acc, cur) => {
      const id = cur.__row_id__
      if (id) {
        rowId = id
      } else {
        rowId = Number(rowId) + 1
      }

      // build tabular data
      tabularData[`tr_${rowId}`] = Object.fromEntries(
        Object.entries(cur).filter(([k]) => k !== '__row_id__'),
      )

      // build legacy key/value
      Object.entries(cur).forEach(([k, v]) => {
        if (k !== '__row_id__') acc[`${k}__${rowId}`] = v
      })

      seq.push({ row: String(rowId), is_header: false, header_value: '' })
      return acc
    }, {})

    payload[`tf_${field.id}`] = tabularData
    payload[`f_${field.id}`] = seq.length

    await onSave(payload, seq, needCleanInsert)
    setLoading(false)
  }

  /* ================= UI ================= */

  return (
    <>
      <LoadingOverlay loading={loading}>
        <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="lg">
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px',
            }}
          >
            <Typography fontWeight={600}>Upload Excel Data</Typography>
            <Box>
              {excelRowData?.length > 0 && (
                <Button
                  sx={{ mr: 2 }}
                  onClick={() => {
                    setExcelRowData([])
                    setErrors([])
                  }}
                >
                  Clear Data & Re-upload
                </Button>
              )}
              <Tooltip title="Download Template (Including Data)">
                <IconButton size="small" onClick={downloadExcelTemplate}>
                  <FileDownload fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </DialogTitle>

          <DialogContent dividers>
            <Box>
              {excelRowData.length > 0 ? (
                <Box sx={{ height: 400 }}>
                  <TableVirtuoso
                    data={excelRowData}
                    computeItemKey={(_, row) => row.__rowKey__}
                    fixedHeaderContent={() => (
                      <>
                        <TableRow>
                          {prefilledColumns.length > 0 && (
                            <TableCell colSpan={prefilledColumns.length}>
                              <Typography fontWeight={600}>Existing Data</Typography>
                            </TableCell>
                          )}

                          <TableCell />

                          <TableCell colSpan={excelHeaders.length}>
                            <Typography fontWeight={600}>Uploaded Data</Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow style={{ background: 'white' }}>
                          {/* LEFT (Existing) */}
                          {prefilledColumns.map((col) => (
                            <TableCell>{col.field_template_name}</TableCell>
                          ))}

                          {/* DELETE / DIVIDER */}
                          <TableCell sx={{ width: 40, background: '#fafafa' }} />

                          {/* RIGHT (Uploaded Mapping) */}
                          {excelHeaders.map((h) => (
                            <TableCell key={h.colIndex}>
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                style={{
                                  width: `${(excelColMap[h.colIndex]?.subfield_column_width_in_rem || 12) * 14}px`,
                                }}
                              >
                                <Autocomplete
                                  disableClearable
                                  size="small"
                                  options={editableColumns}
                                  value={
                                    columns.find((c) => c.id === excelColMap[h.colIndex]) || null
                                  }
                                  getOptionLabel={(o) => o.field_template_name}
                                  onChange={(_, v) =>
                                    setExcelColMap((p) => ({
                                      ...p,
                                      [h.colIndex]: v?.id || null,
                                    }))
                                  }
                                  renderInput={(params) => (
                                    <TextField {...params} error={!excelColMap[h.colIndex]} />
                                  )}
                                  sx={{ width: '100%' }}
                                />

                                {/* ✅ DELETE COLUMN BUTTON */}
                                <IconButton size="small" onClick={() => deleteColumn(h.colIndex)}>
                                  <DeleteOutline fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                          ))}
                        </TableRow>
                      </>
                    )}
                    itemContent={(index, row) => {
                      const merged = mergedRows[index]

                      return (
                        <>
                          {/* LEFT (Existing - READ ONLY) */}
                          {prefilledColumns.map((col) => {
                            const val = merged.existingData?.[`f_${col.id}`]

                            return (
                              <TableCell
                                sx={{
                                  background: '#f5f5f5',
                                  color: '#777',
                                }}
                              >
                                {col.data_structure === FIELD_TYPES.LIST
                                  ? listIdValueMap[col.id]?.[val] || 'NA'
                                  : val}
                              </TableCell>
                            )
                          })}

                          {/* DELETE BUTTON */}
                          <TableCell sx={{ background: '#fafafa' }}>
                            {merged.uploadedRowKey && (
                              <IconButton onClick={() => deleteRow(merged.uploadedRowKey)}>
                                <DeleteOutline />
                              </IconButton>
                            )}
                          </TableCell>

                          {/* RIGHT (Uploaded Editable) */}
                          {excelHeaders.map((h) => {
                            const col = columns.find((c) => c.id === excelColMap[h.colIndex])
                            let val = merged.uploadedData?.[h.colIndex]

                            if (typeof val == 'object') {
                              val = val?.toString() || ''
                            }

                            if (FIELD_TYPES.DATE === col?.data_structure) {
                              val = moment(val).format(FORMATS.DATE)
                            } else if (FIELD_TYPES.DATETIME === col?.data_structure) {
                              val = moment(val).format(FORMATS.DATETIME)
                            }

                            const rowKey = merged.uploadedRowKey
                            const isAutoCorrected = !!autoCorrectedCells?.[rowKey]?.[h.colIndex]
                            const matchMeta = listMatchMeta?.[rowKey]?.[h.colIndex]

                            const isMismatch =
                              matchMeta &&
                              matchMeta.percentage < 100 &&
                              !matchMeta.resolved &&
                              !autoCorrectedCells?.[rowKey]?.[h.colIndex]

                            const hasError = errors.some(
                              (err) =>
                                err.rowKey === rowKey &&
                                String(err.colIndex) === String(h.colIndex),
                            )

                            return (
                              <TableCell
                                sx={{
                                  background: hasError
                                    ? '#fff1f0'
                                    : isAutoCorrected
                                      ? '#e6ffed'
                                      : isMismatch
                                        ? '#fff7e6'
                                        : '#ffffff',
                                  border: isAutoCorrected
                                    ? '1px solid #b7eb8f'
                                    : isMismatch
                                      ? '1px solid #ffd591'
                                      : undefined,
                                  cursor: isAutoCorrected ? 'context-menu' : 'default',
                                }}
                                onContextMenu={(e) => {
                                  if (!isAutoCorrected) return
                                  e.preventDefault()
                                  revertAutoMatch(rowKey, h.colIndex)
                                }}
                              >
                                <Tooltip
                                  title={
                                    isAutoCorrected
                                      ? `Auto-corrected from "${autoCorrectedCells[rowKey]?.[h.colIndex]?.originalValue}". Right click to revert`
                                      : isMismatch && !matchMeta?.resolved
                                        ? `Did you mean "${matchMeta.label}"?`
                                        : ''
                                  }
                                >
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Typography style={{ fontSize: 12 }}>{val}</Typography>

                                    {isMismatch && (
                                      <Typography
                                        sx={{
                                          fontSize: 12,
                                          color: 'orange',
                                          cursor: 'pointer',
                                          fontWeight: 600,
                                        }}
                                        onClick={() => applyBestMatch(rowKey, h.colIndex)}
                                      >
                                        ⚠️
                                      </Typography>
                                    )}
                                  </Box>
                                </Tooltip>
                              </TableCell>
                            )
                          })}
                        </>
                      )
                    }}
                    components={{
                      Table: (props) => <Table {...props} stickyHeader />,
                    }}
                  />
                </Box>
              ) : (
                <Box>
                  <Box
                    onClick={() => fileInputRef.current.click()}
                    onDragOver={(e) => {
                      e.preventDefault()
                      setIsDragging(true)
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault()
                      setIsDragging(false)
                      handleExcelUpload({ target: { files: e.dataTransfer.files } })
                    }}
                    sx={{
                      borderRadius: 2,
                      border: '2px dashed',
                      borderColor: isDragging ? '#6200ad' : '',
                      backgroundColor: isDragging ? '#fbf5ff' : '',
                      p: 4,
                      textAlign: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <CloudUpload fontSize="large" />
                    <Typography>Drag & drop Excel or click</Typography>
                    <input ref={fileInputRef} type="file" hidden onChange={handleExcelUpload} />
                  </Box>
                </Box>
              )}
            </Box>
            <Box maxHeight={60} overflow="auto" mt={2}>
              {errors.map((e, i) => (
                <Typography key={i} color="error" fontSize={13}>
                  • Row {e.rowIndex + 1}, Column {e.colIndex}: {e.message}
                </Typography>
              ))}
              {hasDuplicateMappedColumns() && (
                <Typography key="duplicate-mapping" color="error" fontSize={13}>
                  • Multiple columns are mapped to the same field. Please ensure each field is
                  selected only once.
                </Typography>
              )}
              {!areAllExcelColumnsMapped() && (
                <Typography key="missing-mapping" color="error" fontSize={13}>
                  • Some columns are not mapped. Please select a field for all columns.
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions style={{ padding: 16 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={<SaveAlt />}
              disabled={!canSave()}
              onClick={() => processExcelData(excelRowData)}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </LoadingOverlay>
      <ConfirmDialog />
    </>
  )
}

export default UploadExcelDataExtended
